import apiClient from '../api/client';
import { ECPT_ENDPOINTS } from '../api/endpoints';
import type {
  FeedbackSummaryParams,
  FeedbackSummaryResponse,
  GetFeedbackRecipientsParams,
  GetFeedbackRecipientsResponse,
  CreateUserRecipientBody,
  CreateUserRecipientResponse,
  UpdateUserRecipientBody,
  UpdateUserRecipientResponse,
  DeleteRecipientParams,
  DeleteRecipientResponse,
  CreateEmailGroupBody,
  CreateEmailGroupResponse,
  UpdateEmailGroupBody,
  UpdateEmailGroupResponse,
  DeleteEmailGroupParams,
  DeleteEmailGroupResponse,
  GetFeedbackParams,
  GetFeedbackResponse,
  CreateFeedbackBody,
  CreateFeedbackResponse,
  CreateFeedbackMessageBody,
  CreateFeedbackMessageResponse,
  PatchFeedbackStatusBody,
  PatchFeedbackStatusResponse,
} from '../../types/api';

const ecptFeedbackService = {
  // ---------------------------------------------------------------------------
  // Feedback conversations
  // ---------------------------------------------------------------------------

  /** GET /feedback — load all visible conversations for a PEM ID */
  getFeedback: async (
    params: GetFeedbackParams,
  ): Promise<GetFeedbackResponse> => {
    const { data } = await apiClient.get<GetFeedbackResponse>(
      ECPT_ENDPOINTS.FEEDBACK,
      { params },
    );
    return data;
  },

  /** POST /feedback — create a new conversation with initial message */
  createFeedback: async (
    body: CreateFeedbackBody,
  ): Promise<CreateFeedbackResponse> => {
    const { data } = await apiClient.post<CreateFeedbackResponse>(
      ECPT_ENDPOINTS.FEEDBACK,
      body,
    );
    return data;
  },

  /** POST /feedback/{conversationId}/reply — reply to an existing conversation */
  createFeedbackMessage: async (
    conversationId: string,
    body: CreateFeedbackMessageBody,
  ): Promise<CreateFeedbackMessageResponse> => {
    const { data } = await apiClient.post<CreateFeedbackMessageResponse>(
      ECPT_ENDPOINTS.FEEDBACK_REPLY(conversationId),
      body,
    );
    return data;
  },

  /** PATCH /feedback/{conversationId}/status — update conversation status */
  updateFeedbackStatus: async (
    conversationId: string,
    body: PatchFeedbackStatusBody,
  ): Promise<PatchFeedbackStatusResponse> => {
    const { data } = await apiClient.patch<PatchFeedbackStatusResponse>(
      ECPT_ENDPOINTS.FEEDBACK_STATUS(conversationId),
      body,
    );
    return data;
  },

  /** POST /feedback/{conversationId}/summary — AI summary of a conversation */
  summarizeFeedback: async (
    conversationId: string,
    params: FeedbackSummaryParams,
  ): Promise<FeedbackSummaryResponse> => {
    const { data } = await apiClient.post<FeedbackSummaryResponse>(
      ECPT_ENDPOINTS.FEEDBACK_SUMMARY(conversationId),
      null,
      { params },
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Landing
  // ---------------------------------------------------------------------------

  /**
   * GET /settings/feedback-recipients
   * Returns all user recipients and email groups for the logged-in user.
   */
  getAll: async (
    params: GetFeedbackRecipientsParams,
  ): Promise<GetFeedbackRecipientsResponse> => {
    const { data } = await apiClient.get<GetFeedbackRecipientsResponse>(
      ECPT_ENDPOINTS.FEEDBACK_RECIPIENTS,
      { params },
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // User Recipients
  // ---------------------------------------------------------------------------

  /** POST /settings/feedback-recipients/user-recipients */
  createUserRecipient: async (
    body: CreateUserRecipientBody,
  ): Promise<CreateUserRecipientResponse> => {
    const { data } = await apiClient.post<CreateUserRecipientResponse>(
      ECPT_ENDPOINTS.FEEDBACK_USER_RECIPIENTS,
      body,
    );
    return data;
  },

  /** PUT /settings/feedback-recipients/user-recipients/{id} */
  updateUserRecipient: async (
    id: string,
    body: UpdateUserRecipientBody,
    params: DeleteRecipientParams,
  ): Promise<UpdateUserRecipientResponse> => {
    const { data } = await apiClient.put<UpdateUserRecipientResponse>(
      ECPT_ENDPOINTS.FEEDBACK_USER_RECIPIENT(id),
      body,
      { params },
    );
    return data;
  },

  /** DELETE /settings/feedback-recipients/user-recipients/{id} */
  deleteUserRecipient: async (
    id: string,
    params: DeleteRecipientParams,
  ): Promise<DeleteRecipientResponse> => {
    const { data } = await apiClient.delete<DeleteRecipientResponse>(
      ECPT_ENDPOINTS.FEEDBACK_USER_RECIPIENT(id),
      { params },
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Email Groups
  // ---------------------------------------------------------------------------

  /** POST /settings/feedback-recipients/email-groups */
  createEmailGroup: async (
    body: CreateEmailGroupBody,
  ): Promise<CreateEmailGroupResponse> => {
    const { data } = await apiClient.post<CreateEmailGroupResponse>(
      ECPT_ENDPOINTS.FEEDBACK_EMAIL_GROUPS,
      body,
    );
    return data;
  },

  /** PUT /settings/feedback-recipients/email-groups/{id} */
  updateEmailGroup: async (
    id: string,
    body: UpdateEmailGroupBody,
    params: DeleteEmailGroupParams,
  ): Promise<UpdateEmailGroupResponse> => {
    const { data } = await apiClient.put<UpdateEmailGroupResponse>(
      ECPT_ENDPOINTS.FEEDBACK_EMAIL_GROUP(id),
      body,
      { params },
    );
    return data;
  },

  /** DELETE /settings/feedback-recipients/email-groups/{id} */
  deleteEmailGroup: async (
    id: string,
    params: DeleteEmailGroupParams,
  ): Promise<DeleteEmailGroupResponse> => {
    const { data } = await apiClient.delete<DeleteEmailGroupResponse>(
      ECPT_ENDPOINTS.FEEDBACK_EMAIL_GROUP(id),
      { params },
    );
    return data;
  },
};

export default ecptFeedbackService;
